import { useState } from 'react';
import { Box, Select, TextField, Typography } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../components/Panel';
import { FACTS } from '../facts';

/** IRS standard mileage rate — update yearly; the guide links Pub. 463 for the official figure. */
const MILEAGE_RATE_USD = 0.7;

/** Light client-side weekly set-aside calculator (spec §16 tool #2). */
export function Estimator() {
  const { t } = useTranslation();
  const [income, setIncome] = useState('400');
  const [miles, setMiles] = useState('120');
  const [rate, setRate] = useState(String(FACTS.taxSetAsidePct.high));
  const inc = Math.max(0, Number(income) || 0);
  const mi = Math.max(0, Number(miles) || 0);
  const pct = Math.max(0, Number(rate) || 0) / 100;
  const weeklyProfit = Math.max(0, inc - mi * MILEAGE_RATE_USD);
  const weeklySetAside = weeklyProfit * pct;
  const money = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <Panel title={t('estimator.title')} accent="#2E9E3A">
      <Typography sx={{ color: '#4b5563', fontSize: '14px', mb: 2 }}>{t('estimator.intro')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
        <TextField label={t('estimator.weeklyIncome')} ariaLabel={t('estimator.weeklyIncome')} size="small" type="number" value={income} onChange={(e) => setIncome(e.target.value)} />
        <TextField label={t('estimator.weeklyMiles')} ariaLabel={t('estimator.weeklyMiles')} size="small" type="number" value={miles} onChange={(e) => setMiles(e.target.value)} />
        <Select ariaLabel={t('estimator.rate')} label={t('estimator.rate')} native size="small" value={rate} onChange={(_e, v) => setRate(String(v))} options={[{ value: '25', label: '25%' }, { value: '30', label: '30%' }, { value: '35', label: '35%' }]} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        <Stat label={t('estimator.result.weekly')} value={money(weeklySetAside)} strong />
        <Stat label={t('estimator.result.quarterly')} value={money(weeklySetAside * 13)} />
        <Stat label={t('estimator.result.taxable')} value={money(weeklyProfit * 13)} />
      </Box>
      <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 2 }}>{t('estimator.note')} · {MILEAGE_RATE_USD.toFixed(2)} $/mi</Typography>
    </Panel>
  );
}
function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: strong ? '#ecfdf3' : '#f8fafc' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '22px', color: strong ? '#15803d' : '#1f2937' }}>{value}</Typography>
      <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{label}</Typography>
    </Box>
  );
}
